import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';

import type { PaywallDetail } from '../../services/api/billing';

interface UpgradePromptProps {
  paywall: PaywallDetail | null;
  onClose: () => void;
}

/**
 * Shown when the API answers 402. The point is to convert, so it names the
 * limit that was hit and what lifting it costs, rather than showing an error.
 */
const UpgradePrompt: React.FC<UpgradePromptProps> = ({ paywall, onClose }) => (
  <AnimatePresence>
    {paywall && (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          role="dialog"
          aria-modal="true"
          className="fixed z-50 inset-x-4 top-1/2 -translate-y-1/2 mx-auto max-w-md bg-white rounded-3xl shadow-2xl p-8"
        >
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 p-2 rounded-xl hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>

          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-xs font-semibold mb-4">
            <SparklesIcon className="w-4 h-4" />
            Upgrade to continue
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            You've reached your free limit
          </h2>
          <p className="text-gray-600 mb-6">{paywall.message}</p>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/pricing"
              className="flex-1 text-center px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-lg transition-all"
            >
              See plans
            </Link>
            <button
              onClick={onClose}
              className="px-6 py-3 rounded-xl font-medium text-gray-600 hover:bg-gray-100 transition-colors"
            >
              Not now
            </button>
          </div>

          <p className="text-xs text-gray-400 mt-4 text-center">
            Passes expire on their own. Nothing renews automatically.
          </p>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

export default UpgradePrompt;
